import CameraIcon from '@mui/icons-material/Camera';
import { useCallback, useRef } from 'react';
import Draggable from 'react-draggable';
import { toast } from 'react-toastify';
import Webcam from 'react-webcam';
import API from 'api/API';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import SwitchCameraIcon from 'components/SwitchCameraIcon';
import { useLanguage } from 'context/AppContext';
import useCameras from 'hooks/useCameras';
import useScreenSize from 'hooks/useScreenSize';
import { dataUriToBlob } from 'utils/helpers/dataUriToBlob';
import { fileToDataURI } from 'utils/helpers/fileToDataUri';
import { optimiseImage } from 'utils/optimiseImage';
import * as Styles from './Camera.styles';

type Props = {
  updateCapturedImage: (optimisedImageDataUri: string) => void;
  heightDefault?: boolean;
};

export const Camera = ({ updateCapturedImage, heightDefault }: Props) => {
  const IMAGE_QUALITY = 0.85;
  const MAX_IMAGE_WIDTH = 1300;
  const MAX_IMAGE_HEIGHT = 731;

  const screenSize = useScreenSize();
  const isLandscape = screenSize.width >= screenSize.height;
  const aspectRatio = isLandscape ? MAX_IMAGE_WIDTH / MAX_IMAGE_HEIGHT : MAX_IMAGE_HEIGHT / MAX_IMAGE_WIDTH;

  const lang = useLanguage()[0].TrayImageCapture;

  const { detectCameras, switchCamera, camera, cameras } = useCameras();

  const onUserMedia = useCallback(detectCameras, [detectCameras]);

  const onUserMediaError = useCallback(() => {
    toast.warn(lang.imageCaptureFailed);
  }, [lang.imageCaptureFailed]);

  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return;

    const dataUri = webcamRef.current.getScreenshot();

    if (!dataUri) {
      API.errorHandler(new Error(lang.imageCaptureFailed));
      return;
    }

    const imageBlob = await dataUriToBlob(dataUri);

    const optimisedImageBlob = await optimiseImage({
      file: imageBlob,
      quality: IMAGE_QUALITY,
      // if landscape then max width is 1300px, if portrait then max height is 1300px
      // we use maxX to maintain the aspect ratio
      maxWidth: isLandscape ? MAX_IMAGE_WIDTH : MAX_IMAGE_HEIGHT,
      maxHeight: isLandscape ? MAX_IMAGE_HEIGHT : MAX_IMAGE_WIDTH,
    });

    const optimisedImageDataUri = await fileToDataURI(optimisedImageBlob);

    updateCapturedImage(optimisedImageDataUri); // Save the captured image in state
    //toast.success(lang.imageCaptured);
  }, [isLandscape, lang.imageCaptureFailed, updateCapturedImage]);

  const webcamRef = useRef<Webcam | null>(null);

  return camera ? (
    <>
      <Styles.WebcamBounds heightDefault={heightDefault}>
        {camera.deviceId && (
          <Draggable cancel=".MuiButtonBase-root">
            <Styles.WebcamControls>
              <span className="handle">{camera.label}</span>
              <Styles.CameraIcon onClick={captureImage} title={lang.captureImage}>
                <CameraIcon />
              </Styles.CameraIcon>
              {cameras && cameras.length > 1 && <SwitchCameraIcon switchCamera={switchCamera} />}
            </Styles.WebcamControls>
          </Draggable>
        )}
        <Webcam
          style={{
            width: MAX_IMAGE_WIDTH + 'px',
            maxWidth: '100%',
            height: MAX_IMAGE_HEIGHT + 'px',
            maxHeight: '100%',
          }}
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          videoConstraints={{
            deviceId: camera.deviceId,
            aspectRatio,
            width: MAX_IMAGE_WIDTH,
            height: MAX_IMAGE_HEIGHT,
          }}
          onUserMedia={onUserMedia}
          onUserMediaError={onUserMediaError}
        />
      </Styles.WebcamBounds>
    </>
  ) : (
    <PageSpinner data-testid="page-spinner" />
  );
};
